<template>
  <v-container fluid class="help-page pa-3 pa-md-6">
    <v-card class="help-hero rounded-lg mb-6 overflow-hidden" elevation="0">
      <v-card-text class="pa-5 pa-md-8">
        <v-row align="center">
          <v-col cols="12" md="8">
            <div class="d-flex align-center mb-3">
              <v-avatar color="white" size="58" class="mr-4 elevation-2">
                <v-icon color="primary" size="32">mdi-lifebuoy</v-icon>
              </v-avatar>
              <div class="white--text">
                <h1 class="text-h4 font-weight-bold mb-1">{{ $t('help_center') }}</h1>
                <p class="mb-0 help-hero__description">{{ $t('help_center_description') }}</p>
              </div>
            </div>
            <div v-if="getProfile && getProfile.email" class="white--text text-body-2 help-hero__user">
              <v-icon small dark class="mr-1">mdi-account-circle-outline</v-icon>
              {{ getProfile.email }}
            </div>
          </v-col>
          <v-col cols="12" md="4" class="text-md-right">
            <v-btn color="white" class="primary--text" large depressed rounded @click="openCreateDialog">
              <v-icon left>mdi-ticket-plus-outline</v-icon>{{ $t('create_support_ticket') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="rounded-lg overflow-hidden" elevation="2">
      <v-tabs v-model="activeTab" color="primary" show-arrows>
        <v-tab href="#faq">
          <v-icon left>mdi-frequently-asked-questions</v-icon>{{ $t('frequent_questions') }}
        </v-tab>
        <v-tab href="#tickets">
          <v-badge :content="tickets.length" :value="tickets.length > 0" color="primary" overlap class="mr-3">
            <v-icon left>mdi-ticket-outline</v-icon>
          </v-badge>
          {{ $t('my_support_tickets') }}
        </v-tab>
      </v-tabs>
      <v-divider></v-divider>

      <v-tabs-items v-model="activeTab">
        <v-tab-item value="faq">
          <v-card-text class="pa-4 pa-md-6">
            <div class="faq-intro text-center mx-auto mb-5">
              <h2 class="text-h5 font-weight-bold mb-2">{{ $t('how_can_we_help') }}</h2>
              <p class="grey--text text--darken-1 mb-4">{{ $t('faq_search_description') }}</p>
              <v-text-field
                v-model="faqSearch"
                :label="$t('search_frequent_questions')"
                prepend-inner-icon="mdi-magnify"
                outlined
                rounded
                clearable
                hide-details
              ></v-text-field>
            </div>

            <v-chip-group
              v-model="selectedCategory"
              mandatory
              show-arrows
              active-class="primary white--text"
              class="mb-5"
            >
              <v-chip v-for="category in faqCategories" :key="category.value" :value="category.value" filter>
                <v-icon left small>{{ category.icon }}</v-icon>{{ category.label }}
              </v-chip>
            </v-chip-group>

            <v-expansion-panels v-if="filteredFaqs.length" v-model="expandedFaqs" multiple flat focusable>
              <v-expansion-panel
                v-for="faq in filteredFaqs"
                :key="faq.key"
                class="faq-panel mb-3"
              >
                <v-expansion-panel-header class="font-weight-medium py-4">
                  <div class="d-flex align-center pr-3">
                    <v-avatar :color="categoryColor(faq.category)" size="34" class="mr-3">
                      <v-icon small color="white">{{ categoryIcon(faq.category) }}</v-icon>
                    </v-avatar>
                    <span>{{ faq.question }}</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="faq-answer pt-3 grey--text text--darken-2">{{ faq.answer }}</div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <div v-else class="empty-state text-center py-10">
              <v-avatar color="grey lighten-4" size="72" class="mb-4">
                <v-icon color="grey" size="38">mdi-text-box-search-outline</v-icon>
              </v-avatar>
              <h3 class="text-h6 mb-1">{{ $t('no_questions_found') }}</h3>
              <p class="grey--text mb-4">{{ $t('try_another_search') }}</p>
              <v-btn color="primary" outlined @click="clearFaqFilters">{{ $t('clear_filters') }}</v-btn>
            </div>

            <v-card outlined class="contact-support mt-7 pa-4 pa-md-5 rounded-lg">
              <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between">
                <div class="d-flex align-center mb-4 mb-sm-0">
                  <v-avatar color="primary" size="46" class="mr-3">
                    <v-icon dark>mdi-headset</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold">{{ $t('still_need_help') }}</div>
                    <div class="caption grey--text text--darken-1">{{ $t('support_ticket_prompt') }}</div>
                  </div>
                </div>
                <v-btn color="primary" depressed @click="openCreateDialog">
                  <v-icon left>mdi-ticket-plus-outline</v-icon>{{ $t('new_ticket') }}
                </v-btn>
              </div>
            </v-card>
          </v-card-text>
        </v-tab-item>

        <v-tab-item value="tickets">
          <v-card-text class="pa-4 pa-md-6">
            <v-alert v-if="!ticketsConfigured" type="warning" outlined class="mb-5">
              <div class="font-weight-bold">{{ $t('ticket_service_not_configured') }}</div>
              <div>{{ $t('ticket_service_configuration_help') }}</div>
            </v-alert>

            <v-alert v-if="ticketError" type="error" outlined dismissible @input="ticketError = ''">
              {{ ticketError }}
            </v-alert>

            <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between mb-5">
              <div>
                <h2 class="text-h5 font-weight-bold mb-1">{{ $t('my_support_tickets') }}</h2>
                <p class="grey--text text--darken-1 mb-0">{{ $t('tickets_description') }}</p>
              </div>
              <div class="mt-4 mt-sm-0">
                <v-btn icon color="primary" class="mr-2" :loading="loadingTickets" @click="loadTickets">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
                <v-btn color="primary" depressed @click="openCreateDialog">
                  <v-icon left>mdi-plus</v-icon>{{ $t('new_ticket') }}
                </v-btn>
              </div>
            </div>

            <v-row dense class="mb-4">
              <v-col v-for="stat in ticketStats" :key="stat.label" cols="6" md="3">
                <v-card outlined class="ticket-stat rounded-lg pa-3">
                  <div class="d-flex align-center">
                    <v-avatar :color="stat.color + ' lighten-5'" size="40" class="mr-3">
                      <v-icon :color="stat.color">{{ stat.icon }}</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-h5 font-weight-bold">{{ stat.value }}</div>
                      <div class="caption grey--text">{{ stat.label }}</div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-text-field
              v-model="ticketSearch"
              :label="$t('search_tickets')"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
              class="ticket-search mb-4"
            ></v-text-field>

            <v-data-table
              :headers="ticketHeaders"
              :items="tickets"
              :search="ticketSearch"
              :loading="loadingTickets"
              :no-data-text="$t('no_tickets_yet')"
              :no-results-text="$t('no_search_results')"
              :footer-props="footerProps"
              class="ticket-table"
              @click:row="openDetailsDialog"
            >
              <template #item.reference="{ item }">
                <span class="font-weight-bold primary--text">#{{ item.folio || item.id }}</span>
              </template>
              <template #item.title="{ item }">
                <div class="py-2">
                  <div class="font-weight-medium">{{ item.title }}</div>
                  <div class="caption grey--text text-truncate ticket-description">{{ item.description }}</div>
                </div>
              </template>
              <template #item.status="{ item }">
                <v-chip small dark :color="statusColor(item.status)">
                  <v-icon left x-small>{{ statusIcon(item.status) }}</v-icon>{{ statusLabel(item.status) }}
                </v-chip>
              </template>
              <template #item.area="{ item }">{{ cleanAreaName(item.area) }}</template>
              <template #item.created_date="{ item }">{{ formatDate(item.created_date) }}</template>
              <template #item.actions="{ item }">
                <v-btn icon color="primary" @click.stop="openDetailsDialog(item)">
                  <v-icon>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog v-model="createDialog" persistent scrollable max-width="620px">
      <v-card class="rounded-lg overflow-hidden">
        <v-toolbar color="primary" dark flat>
          <v-icon left>mdi-ticket-plus-outline</v-icon>
          <v-toolbar-title>{{ $t('create_support_ticket') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon :disabled="creatingTicket" @click="closeCreateDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-5">
          <v-alert v-if="!ticketsConfigured" type="warning" text dense>
            {{ $t('ticket_service_configuration_help') }}
          </v-alert>
          <v-form ref="ticketForm" @submit.prevent="submitTicket">
            <v-text-field
              v-model="newTicket.title"
              :label="$t('ticket_subject')"
              prepend-inner-icon="mdi-format-title"
              :error-messages="ticketErrors.title"
              maxlength="120"
              counter="120"
              outlined
              dense
            ></v-text-field>
            <v-select
              v-model="newTicket.area_id"
              :items="areas"
              item-text="displayName"
              item-value="id"
              :label="$t('support_area')"
              prepend-inner-icon="mdi-shape-outline"
              :error-messages="ticketErrors.area_id"
              :loading="loadingAreas"
              outlined
              dense
            ></v-select>
            <v-textarea
              v-model="newTicket.description"
              :label="$t('describe_your_request')"
              prepend-inner-icon="mdi-text-box-outline"
              :error-messages="ticketErrors.description"
              rows="5"
              auto-grow
              maxlength="1200"
              counter="1200"
              outlined
            ></v-textarea>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newTicket.client_name"
                  :label="$t('contact_name')"
                  prepend-inner-icon="mdi-account-outline"
                  outlined
                  dense
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newTicket.client_email"
                  :label="$t('email')"
                  prepend-inner-icon="mdi-email-outline"
                  outlined
                  dense
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>
            <v-alert v-if="createError" type="error" outlined dense>{{ createError }}</v-alert>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn text :disabled="creatingTicket" @click="closeCreateDialog">{{ $t('cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="creatingTicket"
            :disabled="!ticketsConfigured"
            @click="submitTicket"
          >
            <v-icon left>mdi-send-outline</v-icon>{{ $t('send_ticket') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailsDialog" max-width="720px" scrollable>
      <v-card v-if="selectedTicket" class="rounded-lg overflow-hidden">
        <v-toolbar color="primary" dark flat>
          <v-icon left>mdi-ticket-confirmation-outline</v-icon>
          <v-toolbar-title>{{ $t('ticket') }} #{{ selectedTicket.folio || selectedTicket.id }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-5">
          <div class="d-flex flex-column flex-sm-row align-sm-start justify-space-between mb-5">
            <div class="pr-sm-4">
              <h2 class="text-h5 font-weight-bold mb-2">{{ selectedTicket.title }}</h2>
              <div class="grey--text">
                <v-icon small class="mr-1">mdi-calendar-outline</v-icon>{{ formatDate(selectedTicket.created_date) }}
              </div>
            </div>
            <v-chip dark :color="statusColor(selectedTicket.status)" class="mt-3 mt-sm-0">
              {{ statusLabel(selectedTicket.status) }}
            </v-chip>
          </div>
          <v-row dense class="mb-3">
            <v-col cols="12" sm="6">
              <div class="detail-field pa-3">
                <div class="caption grey--text">{{ $t('support_area') }}</div>
                <div class="font-weight-medium">{{ cleanAreaName(selectedTicket.area) }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="detail-field pa-3">
                <div class="caption grey--text">{{ $t('contact_name') }}</div>
                <div class="font-weight-medium">{{ selectedTicket.client_name || $t('not_available') }}</div>
              </div>
            </v-col>
          </v-row>
          <div class="text-subtitle-2 font-weight-bold mb-2">{{ $t('request_description') }}</div>
          <div class="ticket-detail-description pa-4">{{ selectedTicket.description || $t('without_description') }}</div>
          <v-alert v-if="selectedTicket.resolution || selectedTicket.solution" type="success" text class="mt-5 mb-0">
            <div class="font-weight-bold mb-1">{{ $t('support_response') }}</div>
            {{ selectedTicket.resolution || selectedTicket.solution }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">{{ $t('close') }}</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { ticketsService } from '@/services/ticketsService'

export default {
  name: 'Ayuda',
  data: () => ({
    activeTab: 'faq',
    faqSearch: '',
    selectedCategory: 'all',
    expandedFaqs: [],
    ticketSearch: '',
    tickets: [],
    areas: [],
    loadingTickets: false,
    loadingAreas: false,
    creatingTicket: false,
    ticketError: '',
    createError: '',
    ticketErrors: {},
    createDialog: false,
    detailsDialog: false,
    selectedTicket: null,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    newTicket: {
      title: '',
      description: '',
      area_id: null,
      client_name: '',
      client_email: '',
      client_phone: ''
    },
    footerProps: {
      'items-per-page-options': [5, 10, 25, 50]
    }
  }),
  computed: {
    ...mapGetters(['getProfile']),
    ticketsConfigured () {
      return ticketsService.isConfigured
    },
    faqCategories () {
      return [
        { value: 'all', label: this.$t('all_categories'), icon: 'mdi-view-grid-outline', color: 'primary' },
        { value: 'account', label: this.$t('account_and_security'), icon: 'mdi-account-lock-outline', color: 'indigo' },
        { value: 'invitations', label: this.$t('invitations_and_access'), icon: 'mdi-account-multiple-plus-outline', color: 'teal' },
        { value: 'reservations', label: this.$t('reservations_and_areas'), icon: 'mdi-calendar-check-outline', color: 'purple' },
        { value: 'notifications', label: this.$t('notifications_and_notices'), icon: 'mdi-bell-outline', color: 'orange' },
        { value: 'tickets', label: this.$t('support_tickets'), icon: 'mdi-ticket-outline', color: 'blue' }
      ]
    },
    faqs () {
      const definitions = [
        ['password', 'account'],
        ['profile', 'account'],
        ['invitation', 'invitations'],
        ['invitation_approval', 'invitations'],
        ['access_code', 'invitations'],
        ['reservation', 'reservations'],
        ['reservation_cancel', 'reservations'],
        ['notifications', 'notifications'],
        ['notices', 'notifications'],
        ['create_ticket', 'tickets'],
        ['ticket_status', 'tickets'],
        ['ticket_response', 'tickets']
      ]
      return definitions.map(item => ({
        key: item[0],
        category: item[1],
        question: this.$t(`help_faq_${item[0]}_question`),
        answer: this.$t(`help_faq_${item[0]}_answer`)
      }))
    },
    filteredFaqs () {
      let result = this.faqs
      if (this.selectedCategory !== 'all') {
        result = result.filter(faq => faq.category === this.selectedCategory)
      }
      const search = (this.faqSearch || '').trim().toLowerCase()
      if (search) {
        result = result.filter(faq =>
          faq.question.toLowerCase().includes(search) || faq.answer.toLowerCase().includes(search)
        )
      }
      return result
    },
    ticketHeaders () {
      return [
        { text: this.$t('reference'), value: 'reference' },
        { text: this.$t('ticket_subject'), value: 'title' },
        { text: this.$t('support_area'), value: 'area' },
        { text: this.$t('status'), value: 'status' },
        { text: this.$t('creation_date'), value: 'created_date' },
        { text: this.$t('actions'), value: 'actions', sortable: false, align: 'end' }
      ]
    },
    ticketStats () {
      return [
        { label: this.$t('total'), value: this.tickets.length, icon: 'mdi-ticket-outline', color: 'primary' },
        { label: this.$t('new_status'), value: this.countByGroup('new'), icon: 'mdi-alert-circle-outline', color: 'orange' },
        { label: this.$t('in_progress'), value: this.countByGroup('progress'), icon: 'mdi-progress-clock', color: 'blue' },
        { label: this.$t('resolved_status'), value: this.countByGroup('resolved'), icon: 'mdi-check-circle-outline', color: 'green' }
      ]
    }
  },
  watch: {
    getProfile: {
      immediate: true,
      handler (profile) {
        if (profile && profile.email && this.ticketsConfigured) {
          this.loadAreas()
          this.loadTickets()
        }
      }
    }
  },
  methods: {
    categoryIcon (category) {
      const item = this.faqCategories.find(option => option.value === category)
      return item ? item.icon : 'mdi-help-circle-outline'
    },
    categoryColor (category) {
      const item = this.faqCategories.find(option => option.value === category)
      return item ? item.color : 'primary'
    },
    clearFaqFilters () {
      this.faqSearch = ''
      this.selectedCategory = 'all'
      this.expandedFaqs = []
    },
    async loadAreas () {
      if (!this.ticketsConfigured) return
      this.loadingAreas = true
      try {
        const response = await ticketsService.getAreas()
        if (response.success !== false) {
          const areas = Array.isArray(response.data) ? response.data : []
          this.areas = areas.map(area => ({
            ...area,
            displayName: this.cleanAreaName(area.name)
          }))
        }
      } catch (error) {
        this.ticketError = this.apiError(error, this.$t('areas_loading_error'))
      } finally {
        this.loadingAreas = false
      }
    },
    async loadTickets () {
      if (!this.ticketsConfigured || !this.getProfile || !this.getProfile.email) return
      this.loadingTickets = true
      this.ticketError = ''
      try {
        const response = await ticketsService.getTicketsByEmail(this.getProfile.email, 100)
        if (response.success === false) throw new Error(response.message || response.error)
        this.tickets = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.ticketError = this.apiError(error, this.$t('tickets_loading_error'))
      } finally {
        this.loadingTickets = false
      }
    },
    openCreateDialog () {
      this.resetTicketForm()
      const profile = this.getProfile || {}
      this.newTicket.client_name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
      this.newTicket.client_email = profile.email || ''
      this.newTicket.client_phone = profile.celular || profile.cellNumber || profile.phone || ''
      this.createDialog = true
    },
    closeCreateDialog () {
      this.createDialog = false
      this.resetTicketForm()
    },
    resetTicketForm () {
      this.newTicket = {
        title: '',
        description: '',
        area_id: null,
        client_name: '',
        client_email: '',
        client_phone: ''
      }
      this.ticketErrors = {}
      this.createError = ''
    },
    validateTicket () {
      const errors = {}
      if (!this.newTicket.title.trim()) errors.title = [this.$t('field_required')]
      if (!this.newTicket.description.trim()) errors.description = [this.$t('field_required')]
      if (!this.newTicket.area_id) errors.area_id = [this.$t('field_required')]
      this.ticketErrors = errors
      return Object.keys(errors).length === 0
    },
    async submitTicket () {
      if (!this.ticketsConfigured || !this.validateTicket()) return
      this.creatingTicket = true
      this.createError = ''
      try {
        const profile = this.getProfile || {}
        const response = await ticketsService.createTicket({
          title: this.newTicket.title.trim(),
          description: this.newTicket.description.trim(),
          area_id: this.newTicket.area_id,
          client_name: this.newTicket.client_name,
          client_email: this.newTicket.client_email,
          client_phone: this.newTicket.client_phone,
          client_company: profile.housing_unit_name || profile.short_name || ''
        })
        if (response.success === false) throw new Error(response.message || response.error)
        this.closeCreateDialog()
        this.activeTab = 'tickets'
        this.showMessage(this.$t('ticket_created_successfully'), 'success')
        await this.loadTickets()
      } catch (error) {
        const data = error && error.response && error.response.data
        if (data && !data.error && typeof data === 'object') this.ticketErrors = data
        this.createError = this.apiError(error, this.$t('ticket_creation_error'))
      } finally {
        this.creatingTicket = false
      }
    },
    async openDetailsDialog (ticket) {
      this.selectedTicket = ticket
      this.detailsDialog = true
      if (!this.ticketsConfigured || !ticket.id) return
      try {
        const response = await ticketsService.getTicketById(ticket.id)
        if (response.success !== false && response.data) this.selectedTicket = response.data
      } catch (error) {
        // The row already contains enough data to keep the details dialog useful.
      }
    },
    normalizedStatus (status) {
      const value = String(status || '').toLowerCase().trim()
      if (['nuevo', 'new', 'abierto', 'open'].includes(value)) return 'new'
      if (['en progreso', 'in progress', 'progreso', 'pending'].includes(value)) return 'progress'
      if (['resuelto', 'resolved', 'solved'].includes(value)) return 'resolved'
      if (['cerrado', 'closed'].includes(value)) return 'closed'
      return 'other'
    },
    cleanAreaName (name) {
      if (!name) return this.$t('general')
      return String(name).replace(/^\s*\[[^\]]*\]\s*/, '').trim()
    },
    countByGroup (group) {
      return this.tickets.filter(ticket => this.normalizedStatus(ticket.status) === group).length
    },
    statusLabel (status) {
      const labels = {
        new: this.$t('new_status'),
        progress: this.$t('in_progress'),
        resolved: this.$t('resolved_status'),
        closed: this.$t('closed_status')
      }
      return labels[this.normalizedStatus(status)] || status || this.$t('not_available')
    },
    statusColor (status) {
      const colors = { new: 'orange', progress: 'blue', resolved: 'green', closed: 'grey', other: 'grey' }
      return colors[this.normalizedStatus(status)]
    },
    statusIcon (status) {
      const icons = {
        new: 'mdi-alert-circle-outline',
        progress: 'mdi-progress-clock',
        resolved: 'mdi-check-circle-outline',
        closed: 'mdi-lock-outline',
        other: 'mdi-help-circle-outline'
      }
      return icons[this.normalizedStatus(status)]
    },
    formatDate (value) {
      if (!value) return this.$t('not_available')
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return new Intl.DateTimeFormat(this.$i18n.locale === 'es' ? 'es-MX' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },
    apiError (error, fallback) {
      const response = error && error.response
      const data = response && response.data
      return (data && (data.message || data.error || data.detail)) || error.message || fallback
    },
    showMessage (message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.help-page {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.help-hero {
  background: linear-gradient(125deg, var(--v-primary-base), #254879);
}

.help-hero__description,
.help-hero__user {
  opacity: 0.9;
}

.faq-intro {
  max-width: 720px;
}

.faq-panel {
  border: 1px solid #e4e8ef;
  border-radius: 10px !important;
  overflow: hidden;
}

.faq-panel::before {
  box-shadow: none !important;
}

.faq-answer {
  line-height: 1.7;
}

.contact-support {
  border-color: #dce5f4 !important;
  background: #f7f9fd;
}

.ticket-stat {
  height: 100%;
  border-color: #e4e8ef !important;
}

.ticket-search {
  max-width: 430px;
}

.ticket-table >>> tbody tr {
  cursor: pointer;
}

.ticket-description {
  max-width: 340px;
}

.detail-field,
.ticket-detail-description {
  height: 100%;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  background: #f8f9fb;
}

.ticket-detail-description {
  white-space: pre-line;
  line-height: 1.65;
}

@media (max-width: 599px) {
  .ticket-search {
    max-width: none;
  }
}
</style>
